import { Fragment, useState } from 'react';
import clsx from 'clsx';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Nil, Table, THead, Td, Th, Tr } from '../../components/ui/Table';
import { InfoTip } from '../../components/ui/InfoTip';
import { ErrorState, LoadingState, errorMessage } from '../../components/ui/States';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { useAccess } from '../../hooks/useAccess';
import { usePoints, useSetPoints } from '../../hooks/useSignalOps';
import type { PointsRow, ScoreSettingRow } from '../../api/signalOps';

/**
 * Points — what every behaviour is worth, and the rules around the score.
 *
 * Walter and Lea set the points here (replacing the claude.ai sheet, 14 Oct
 * 2026). The score is the engine of the funnel: an ICP company whose score
 * reaches the MQA threshold becomes MQA. Points win over the learning model,
 * which only suggests. Every change is versioned with who and why.
 */

const HOW_IT_WORKS = (
  <>
    <p><strong>Points add up</strong> on the day a company does something; negative points subtract.</p>
    <p className="mt-1.5"><strong>Points fade</strong>: they halve every few weeks (setting below), so a company that goes quiet cools down.</p>
    <p className="mt-1.5"><strong>Fit scales the total</strong>: an out-of-ICP company still shows, just lower. Things we cause (an ad shown, an email sent) count at a tenth.</p>
    <p className="mt-1.5"><strong>Repeats count less</strong>: the second day with the same signal is worth 71%, the fourth 50%.</p>
    <p className="mt-1.5"><strong>The score places companies in the funnel</strong>: core or adjacent ICP companies at or above the MQA threshold become MQA. A demo request, a deal or a contract decide the stages after that, whatever the score.</p>
    <p className="mt-1.5">Changes apply on the next nightly run (08:15), or when an admin runs the pipeline from Data health.</p>
  </>
);

const CAPTURED: Record<string, { label: string; variant: 'success' | 'warning' | 'default' }> = {
  live: { label: 'Captured', variant: 'success' },
  no_data: { label: 'No data yet', variant: 'warning' },
  no_emitter: { label: 'Not captured', variant: 'default' },
};

function signed(n: number): string {
  const v = Math.round(n * 10) / 10;
  return v > 0 ? `+${v}` : String(v);
}

function Editor({
  value,
  min,
  max,
  onSave,
  onCancel,
  pending,
  error,
}: {
  value: number;
  min: number;
  max: number;
  onSave: (value: number, rationale: string) => void;
  onCancel: () => void;
  pending: boolean;
  error: string | null;
}) {
  const [v, setV] = useState(String(value));
  const [why, setWhy] = useState('');
  const n = Number(v);
  const valid = v.trim() !== '' && Number.isFinite(n) && n >= min && n <= max && why.trim().length >= 3;
  return (
    <div className="grid gap-2 bg-surface-secondary px-4 py-3 sm:grid-cols-[7rem_1fr_auto]">
      <Input id="points-value" aria-label="New value" type="number" step="0.5" value={v} onChange={(e) => setV(e.target.value)} />
      <Input id="points-why" aria-label="Why" placeholder="Why this value? (required — kept in the history)" value={why} onChange={(e) => setWhy(e.target.value)} />
      <div className="flex gap-2">
        <Button size="sm" disabled={!valid || pending} isLoading={pending} onClick={() => onSave(n, why.trim())}>Save</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
      {error && <p className="text-body-sm text-error-dark sm:col-span-3">Not saved: {error}</p>}
      {!error && v.trim() !== '' && (!Number.isFinite(n) || n < min || n > max) && (
        <p className="text-body-xs text-error-dark sm:col-span-3">Enter a number between {min} and {max}.</p>
      )}
    </div>
  );
}

function Settings({ rows, canEdit }: { rows: ScoreSettingRow[]; canEdit: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const save = useSetPoints();
  return (
    <Card padding="none">
      <div className="px-4 pt-4 pb-3">
        <h3 className="text-body-md font-semibold text-text-primary">Rules around the score</h3>
        <p className="text-body-xs text-text-tertiary">How fast points fade, the heat bands, and the score that makes an ICP company MQA.</p>
      </div>
      <Table minWidth={640} colWidths={['16rem', '6rem', '6rem', 'auto', '5rem']}>
        <THead>
          <tr>
            <Th className="pl-4">Setting</Th>
            <Th align="right">Value</Th>
            <Th align="right">Default</Th>
            <Th>Last set</Th>
            <Th />
          </tr>
        </THead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.key}>
              <Tr>
                <Td className="pl-4">
                  <span className="font-medium text-text-primary">{r.label}</span>
                  <span className="block text-body-xs text-text-tertiary">{r.help}</span>
                </Td>
                <Td align="right" className="font-mono tabular-nums text-text-primary">{formatNumber(r.value)}</Td>
                <Td align="right" className="font-mono tabular-nums text-text-tertiary">{formatNumber(r.default)}</Td>
                <Td className="text-text-secondary" title={r.rationale ?? undefined}>
                  {r.setBy ? `${r.setBy} · ${formatDayMonthYear(r.setAt!)}` : <span className="text-text-tertiary">Default</span>}
                </Td>
                <Td align="right" className="pr-4">
                  {canEdit && editing !== r.key && <Button size="sm" variant="ghost" onClick={() => { save.reset(); setEditing(r.key); }}>Edit</Button>}
                </Td>
              </Tr>
              {editing === r.key && (
                <tr>
                  <td colSpan={5}>
                    <Editor
                      value={r.value}
                      min={0.5}
                      max={10_000}
                      pending={save.isPending}
                      error={save.isError ? errorMessage(save.error) : null}
                      onCancel={() => setEditing(null)}
                      onSave={(value, rationale) => save.mutate({ kind: 'setting', key: r.key, value, rationale }, { onSuccess: () => setEditing(null) })}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function Signals({ rows, canEdit }: { rows: PointsRow[]; canEdit: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const save = useSetPoints();
  const groups = new Map<string, PointsRow[]>();
  for (const r of rows) groups.set(r.group, [...(groups.get(r.group) ?? []), r]);

  return (
    <Card padding="none">
      <div className="px-4 pt-4 pb-3">
        <h3 className="text-body-md font-semibold text-text-primary">Points per signal</h3>
        <p className="text-body-xs text-text-tertiary">
          What each behaviour adds (or removes) on the day it happens. "Not captured" signals score nothing until something records them. The model's suggestion is shown, never applied by itself.
        </p>
      </div>
      <Table minWidth={900} colWidths={['auto', '7rem', '5rem', '5rem', '6rem', '10rem', '5rem']}>
        <THead>
          <tr>
            <Th className="pl-4">Signal</Th>
            <Th>Today</Th>
            <Th align="right">Points</Th>
            <Th align="right">Default</Th>
            <Th align="right" title="What the learning model would set, once it has enough demos to say">Model says</Th>
            <Th>Last set</Th>
            <Th />
          </tr>
        </THead>
        <tbody>
          {[...groups.entries()].map(([group, list]) => (
            <Fragment key={group}>
              <tr>
                <td colSpan={7} className="bg-surface-secondary px-4 py-1.5 text-eyebrow uppercase text-text-tertiary">{group}</td>
              </tr>
              {list.map((r) => {
                const cap = CAPTURED[r.captured] ?? CAPTURED.no_emitter!;
                return (
                  <Fragment key={r.signalType}>
                    <Tr>
                      <Td className="pl-4">
                        <span className="font-medium text-text-primary">{r.label}</span>
                        <span className="block text-body-xs text-text-tertiary">
                          {r.channelLabel}
                          {r.intentClass === 'reach' && ' · our action, counted at a tenth'}
                          {r.intentClass === 'trigger' && ' · market signal'}
                        </span>
                      </Td>
                      <Td title={r.ledgerRows ? `${formatNumber(r.ledgerRows)} signals on ${formatNumber(r.accounts)} companies${r.newestAt ? ` · newest ${formatDayMonthYear(r.newestAt)}` : ''}` : undefined}>
                        <Badge variant={cap.variant}>{cap.label}</Badge>
                      </Td>
                      <Td align="right" className={clsx('font-mono tabular-nums', r.points < 0 ? 'text-error-dark' : 'text-text-primary', r.tuned && 'font-semibold')}>
                        {signed(r.points)}
                      </Td>
                      <Td align="right" className="font-mono tabular-nums text-text-tertiary">{signed(r.defaultPoints)}</Td>
                      <Td align="right" className="font-mono tabular-nums text-text-secondary" title={r.suggestedFrom ? `From ${r.suggestedFrom} companies` : undefined}>
                        {r.suggested == null ? <Nil /> : signed(r.suggested)}
                      </Td>
                      <Td className="text-text-secondary" title={r.rationale ?? undefined}>
                        {r.tuned && r.setBy ? `${r.setBy.split('@')[0]} · ${formatDayMonthYear(r.setAt!)}` : <span className="text-text-tertiary">Default</span>}
                      </Td>
                      <Td align="right" className="pr-4">
                        {canEdit && editing !== r.signalType && (
                          <Button size="sm" variant="ghost" onClick={() => { save.reset(); setEditing(r.signalType); }}>Edit</Button>
                        )}
                      </Td>
                    </Tr>
                    {editing === r.signalType && (
                      <tr>
                        <td colSpan={7}>
                          <Editor
                            value={r.points}
                            min={-100}
                            max={100}
                            pending={save.isPending}
                            error={save.isError ? errorMessage(save.error) : null}
                            onCancel={() => setEditing(null)}
                            onSave={(points, rationale) =>
                              save.mutate({ kind: 'signal', signalType: r.signalType, points, rationale }, { onSuccess: () => setEditing(null) })
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export function PointsView() {
  const { isAdmin } = useAccess();
  const { data, isLoading, isError, error, refetch } = usePoints();

  if (isLoading) return <LoadingState rows={6} />;
  if (isError || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const tuned = data.signals.filter((s) => s.tuned).length;
  const notCaptured = data.signals.filter((s) => s.captured !== 'live').length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="The score behind the funnel"
          description={
            <>
              Every company gets one score from these points. It is the heat band on Accounts and it places companies in the Funnel.
              {' '}{tuned} of {data.signals.length} signals carry points set here; {notCaptured} are not captured yet.
              {!isAdmin && ' Ask an admin to change a value.'}
            </>
          }
          action={<InfoTip>{HOW_IT_WORKS}</InfoTip>}
        />
      </Card>
      <Settings rows={data.settings} canEdit={isAdmin} />
      <Signals rows={data.signals} canEdit={isAdmin} />
    </div>
  );
}
